import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import PatientDocumentThumbnail from '../clinic/PatientDocumentThumbnail';
import { useClinicalImageAttachFlow } from '../common/ClinicalImageAttach';
import type { EvolutionAdditionAttachmentDoc, EvolutionAdditionAttachmentImage } from '../../utils/evolutionAdditionPolicy';
import { evolutionCaptionMutedSx, evolutionSecondaryOutlinedButtonSx } from './evolutionTheme';

function defaultTitleFromFile(file: File): string {
  const base = file.name.replace(/\.[^/.]+$/, '').trim();
  return base || file.name;
}

type ReadonlyProps = {
  documents?: EvolutionAdditionAttachmentDoc[] | null;
  images?: EvolutionAdditionAttachmentImage[] | null;
  compact?: boolean;
};

export function EvolutionAdditionAttachmentsReadonly({ documents, images, compact }: ReadonlyProps) {
  const docs = documents || [];
  const imgs = images || [];
  if (!docs.length && !imgs.length) return null;

  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      {docs.map((doc) => (
        <Box
          key={doc.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: compact ? 0.75 : 1,
            borderRadius: 1,
            border: '1px solid #dbeafe',
            bgcolor: '#f8fafc',
          }}
        >
          <PatientDocumentThumbnail doc={doc as any} size={compact ? 36 : 40} accentColor="#3b82f6" />
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: compact ? '0.75rem' : undefined, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {doc.title || doc.file_name || 'Documento'}
          </Typography>
          {doc.file_url ? (
            <Tooltip title="Abrir">
              <IconButton size="small" href={doc.file_url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ))}
      {imgs.map((img) => (
        <Box
          key={img.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: compact ? 0.75 : 1,
            borderRadius: 1,
            border: '1px solid #dbeafe',
            bgcolor: '#f8fafc',
          }}
        >
          {img.file_url ? (
            <Box
              component="img"
              src={img.file_url}
              alt={img.title || ''}
              sx={{ width: compact ? 36 : 40, height: compact ? 36 : 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : null}
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: compact ? '0.75rem' : undefined, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {img.title || img.file_name || 'Imagem'}
          </Typography>
          {img.file_url ? (
            <Tooltip title="Abrir">
              <IconButton size="small" href={img.file_url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ))}
    </Stack>
  );
}

type EditorProps = {
  patientId: string;
  evolutionId: string;
  additionId: string | null;
  professionalId?: string | null;
  disabled?: boolean;
  /** Após salvar informação nova, dispara envio dos arquivos em espera. */
  pendingUploadAdditionId?: string | null;
  onPendingUploadComplete?: () => void;
  onError?: (message: string) => void;
  onChanged?: () => void;
};

export default function EvolutionAdditionFileAttachments({
  patientId,
  evolutionId,
  additionId,
  professionalId,
  disabled = false,
  pendingUploadAdditionId = null,
  onPendingUploadComplete,
  onError,
  onChanged,
}: EditorProps) {
  const [documents, setDocuments] = useState<EvolutionAdditionAttachmentDoc[]>([]);
  const [images, setImages] = useState<EvolutionAdditionAttachmentImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<File[]>([]);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const docInputRef = useRef<HTMLInputElement>(null);
  const onPickImagesRef = useRef<(files: File[]) => void>(() => {});
  const onErrorRef = useRef(onError);
  const pendingUploadingRef = useRef(false);
  const pendingDocumentsRef = useRef(pendingDocuments);
  const pendingImagesRef = useRef(pendingImages);
  pendingDocumentsRef.current = pendingDocuments;
  pendingImagesRef.current = pendingImages;
  onErrorRef.current = onError;

  const load = useCallback(async () => {
    if (!evolutionId || !additionId) {
      if (!additionId) {
        setDocuments([]);
        setImages([]);
      }
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/evolutions/${evolutionId}/additions`));
      const rows = data?.data || [];
      const row = rows.find((r: { id: string }) => String(r.id) === String(additionId));
      setDocuments(Array.isArray(row?.documents) ? row.documents : []);
      setImages(Array.isArray(row?.images) ? row.images : []);
    } catch {
      onErrorRef.current?.('Não foi possível carregar os anexos.');
    } finally {
      setLoading(false);
    }
  }, [evolutionId, additionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const { openImageSourceChooser, imageSourcePickerNodes } = useClinicalImageAttachFlow({
    onFiles: (files) => onPickImagesRef.current(files),
  });

  const uploadDocumentFiles = async (files: File[], targetAdditionId = additionId) => {
    if (!patientId || !evolutionId || !targetAdditionId || files.length === 0) return;
    setUploading(true);
    const uploaded: EvolutionAdditionAttachmentDoc[] = [];
    let failed = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', defaultTitleFromFile(file));
        fd.append('document_type', file.type.startsWith('image/') ? 'photo' : 'other');
        fd.append('evolution_id', evolutionId);
        fd.append('evolution_addition_id', targetAdditionId);
        try {
          const { data: res } = await axios.post(
            getApiUrl(`/clinic/patients/${patientId}/documents/upload`),
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          if (res?.success && res.data) uploaded.push(res.data);
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (uploaded.length) {
        setDocuments((prev) => [...uploaded, ...prev]);
        onChanged?.();
      }
      if (failed > 0) onErrorRef.current?.(`${failed} arquivo(s) não puderam ser enviados.`);
    } finally {
      setUploading(false);
    }
  };

  const uploadImageFiles = async (files: File[], targetAdditionId = additionId) => {
    if (!patientId || !evolutionId || !targetAdditionId || files.length === 0) return;
    setUploading(true);
    const uploaded: EvolutionAdditionAttachmentImage[] = [];
    let failed = 0;
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const { data: uploadRes } = await axios.post(getApiUrl('/clinic/patient-images/upload'), fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (!uploadRes?.success || !uploadRes.data?.file_url) {
            failed += 1;
            continue;
          }
          const { data: createRes } = await axios.post(getApiUrl('/clinic/patient-images'), {
            patient_id: patientId,
            professional_id: professionalId || null,
            evolution_id: evolutionId,
            evolution_addition_id: targetAdditionId,
            title: defaultTitleFromFile(file),
            image_type: 'clinical_photo',
            file_url: uploadRes.data.file_url,
            file_name: uploadRes.data.file_name || file.name,
            file_type: uploadRes.data.file_type || file.type,
          });
          if (createRes?.success && createRes.data) uploaded.push(createRes.data);
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (uploaded.length) {
        setImages((prev) => [...uploaded, ...prev]);
        onChanged?.();
      }
      if (failed > 0) onErrorRef.current?.(`${failed} imagem(ns) não puderam ser enviadas.`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patients/${patientId}/documents/${docId}`));
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      onChanged?.();
    } catch {
      onErrorRef.current?.('Erro ao remover documento.');
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!window.confirm('Remover esta imagem?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patient-images/${imageId}`));
      setImages((prev) => prev.filter((i) => i.id !== imageId));
      onChanged?.();
    } catch {
      onErrorRef.current?.('Erro ao remover imagem.');
    }
  };

  useEffect(() => {
    if (!pendingUploadAdditionId || pendingUploadingRef.current) return;
    const docs = [...pendingDocumentsRef.current];
    const imgs = [...pendingImagesRef.current];
    if (!docs.length && !imgs.length) {
      onPendingUploadComplete?.();
      return;
    }
    pendingUploadingRef.current = true;
    const targetId = pendingUploadAdditionId;
    void (async () => {
      try {
        if (docs.length) await uploadDocumentFiles(docs, targetId);
        if (imgs.length) await uploadImageFiles(imgs, targetId);
        setPendingDocuments([]);
        setPendingImages([]);
        await load();
      } finally {
        pendingUploadingRef.current = false;
        onPendingUploadComplete?.();
      }
    })();
  }, [pendingUploadAdditionId, load]);

  const sectionDisabled = disabled || uploading || !patientId;
  const total = documents.length + images.length + pendingDocuments.length + pendingImages.length;
  const staging = !additionId;

  const onPickDocuments = (files: File[]) => {
    if (!files.length) return;
    if (!patientId) {
      onErrorRef.current?.('Paciente não identificado — não é possível anexar arquivos.');
      return;
    }
    if (staging) {
      setPendingDocuments((prev) => [...prev, ...files]);
      return;
    }
    void uploadDocumentFiles(files);
  };

  const onPickImages = (files: File[]) => {
    if (!files.length) return;
    if (!patientId) {
      onErrorRef.current?.('Paciente não identificado — não é possível anexar arquivos.');
      return;
    }
    if (staging) {
      setPendingImages((prev) => [...prev, ...files]);
      return;
    }
    void uploadImageFiles(files);
  };

  onPickImagesRef.current = onPickImages;

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            Anexos
          </Typography>
          {staging ? (
            <Typography variant="caption" sx={evolutionCaptionMutedSx}>
              Você pode escolher os arquivos agora; ao salvar o texto, o envio é feito automaticamente.
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <input
            ref={docInputRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              onPickDocuments(files);
            }}
          />
          {imageSourcePickerNodes}
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
            disabled={sectionDisabled}
            onClick={() => docInputRef.current?.click()}
            sx={evolutionSecondaryOutlinedButtonSx}
          >
            Documento
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon />}
            disabled={sectionDisabled}
            onClick={openImageSourceChooser}
            sx={evolutionSecondaryOutlinedButtonSx}
          >
            Imagem
          </Button>
        </Box>
      </Box>

      {!patientId ? (
        <Alert severity="warning" sx={{ mb: 1, fontSize: '0.8rem' }}>
          Não foi possível identificar o paciente desta evolução. Os anexos ficam indisponíveis até o cadastro estar vinculado.
        </Alert>
      ) : null}

      {staging && (pendingDocuments.length > 0 || pendingImages.length > 0) ? (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
          {pendingDocuments.length + pendingImages.length} arquivo(s) aguardando — clique em &quot;Salvar e anexar&quot; para concluir.
        </Alert>
      ) : null}

      {loading && additionId ? (
        <CircularProgress size={22} />
      ) : total === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Nenhum arquivo nesta informação. Use Documento ou Imagem acima.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {pendingDocuments.map((file, idx) => (
            <Box
              key={`pending-doc-${file.name}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: '1px dashed #93c5fd',
                bgcolor: '#eff6ff',
              }}
            >
              <AttachFileIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="primary">Aguardando salvamento</Typography>
              {!disabled && (
                <IconButton
                  size="small"
                  aria-label="Remover"
                  onClick={() => setPendingDocuments((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {pendingImages.map((file, idx) => (
            <Box
              key={`pending-img-${file.name}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: '1px dashed #93c5fd',
                bgcolor: '#eff6ff',
              }}
            >
              <ImageIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="primary">Aguardando salvamento</Typography>
              {!disabled && (
                <IconButton
                  size="small"
                  aria-label="Remover"
                  onClick={() => setPendingImages((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {documents.map((doc) => (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: '1px solid #e2e8f0',
                bgcolor: '#fafafa',
              }}
            >
              <PatientDocumentThumbnail doc={doc as any} size={40} accentColor="#3b82f6" />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {doc.title || doc.file_name || 'Documento'}
              </Typography>
              {doc.file_url ? (
                <Tooltip title="Abrir">
                  <IconButton size="small" href={doc.file_url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {!disabled && (
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => void handleDeleteDocument(doc.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          {images.map((img) => (
            <Box
              key={img.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: '1px solid #e2e8f0',
                bgcolor: '#fafafa',
              }}
            >
              {img.file_url ? (
                <Box
                  component="img"
                  src={img.file_url}
                  alt={img.title || ''}
                  sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                />
              ) : null}
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {img.title || img.file_name || 'Imagem'}
              </Typography>
              {img.file_url ? (
                <Tooltip title="Abrir">
                  <IconButton size="small" href={img.file_url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {!disabled && (
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => void handleDeleteImage(img.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
